import {
  Box,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { GetMyDataHubPullFilesQuery } from "../../../services/api/__generated__/backend_gateway-types";
import dayjs from "../../../utils/dayjs";

type PullFilesReport = NonNullable<
  GetMyDataHubPullFilesQuery["getMyDataHubPullFiles"]
>;
type SourceStatus = PullFilesReport["sources"][number];

const SOURCE_TYPE_NAMES: Record<string, string> = {
  google_drive: "Google Drive",
  airtable: "Airtable",
  netsuite_file_cabinet: "NetSuite File",
  netsuite_suiteql: "NetSuite SuiteQL",
  trackstar_wms: "Trackstar WMS",
  gmail_email_attachment: "Gmail Attachment",
  fulfil: "Fulfil API",
};

const formatBytes = (bytes: number | null | undefined): string => {
  if (bytes == null) return "-";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDateTime = (isoString: string | null | undefined): string =>
  isoString ? dayjs.utc(isoString).local().format("MM/DD/YYYY HH:mm") : "-";

const statusChip = (source: SourceStatus, hasPull: boolean) => {
  if (source.found) {
    return (
      <Chip
        size="small"
        variant="outlined"
        color="success"
        icon={<CheckCircleOutlineIcon />}
        label="Pulled"
      />
    );
  }
  if (source.cadence === "per_run") {
    return (
      <Chip size="small" variant="outlined" label="Pulled at run time" />
    );
  }
  // Before the org's first pull nothing has failed — every daily source is
  // simply unpulled, so don't paint the table red.
  if (!hasPull) {
    return <Chip size="small" variant="outlined" label="No pull yet" />;
  }
  return (
    <Chip
      size="small"
      variant="outlined"
      color="error"
      icon={<ErrorOutlineIcon />}
      label="Missing"
    />
  );
};

export const DataPullsPanelComponent = (props: {
  loading: boolean;
  report: PullFilesReport | null;
}) => {
  const { loading, report } = props;

  const sources = report?.sources || [];
  // Failures surface above the fold; per_run (neutral) sinks below Pulled.
  const rank = (s: SourceStatus) =>
    !s.found && s.cadence !== "per_run" ? 0 : s.found ? 1 : 2;
  const sortedSources = [...sources].sort((a, b) => rank(a) - rank(b));
  const missing = sources.filter((s) => !s.found && s.cadence !== "per_run");
  const dailyCount = sources.filter((s) => s.cadence !== "per_run").length;
  const hasPull = Boolean(report?.pullTimestamp);

  return (
    <Box
      sx={{
        height: "40%",
        minHeight: 180,
        display: "flex",
        flexDirection: "column",
        borderTop: "2px solid #e6eaf1",
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 2,
          py: 1,
          backgroundColor: "#fbfcfe",
          borderBottom: "1px solid #eef1f6",
          flexShrink: 0,
        }}
      >
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.08em",
            color: "text.secondary",
          }}
        >
          DATA PULLS
        </Typography>
        {!loading && report && (
          <>
            {report.pullOverdue ? (
              <Chip
                size="small"
                variant="outlined"
                color="error"
                icon={<ErrorOutlineIcon />}
                label="Pull overdue"
              />
            ) : !hasPull ? (
              <Chip size="small" variant="outlined" label="No pulls yet" />
            ) : missing.length > 0 ? (
              <Chip
                size="small"
                variant="outlined"
                color="error"
                icon={<ErrorOutlineIcon />}
                label={`Pull failed for ${missing.length} file${
                  missing.length === 1 ? "" : "s"
                }`}
              />
            ) : dailyCount > 0 ? (
              <Chip
                size="small"
                variant="outlined"
                color="success"
                icon={<CheckCircleOutlineIcon />}
                label={`All ${dailyCount} daily source${
                  dailyCount === 1 ? "" : "s"
                } pulled`}
              />
            ) : (
              <Chip
                size="small"
                variant="outlined"
                label="All sources pull at run time"
              />
            )}
            {report.pullIsEnabled === false && (
              <Chip size="small" variant="outlined" label="Pulls paused" />
            )}
            <Typography
              sx={{ fontSize: 12, color: "text.secondary", ml: "auto" }}
            >
              {report.pullTimestamp
                ? `Latest daily pull: ${formatDateTime(report.pullTimestamp)}`
                : "No pulled files found yet"}
            </Typography>
          </>
        )}
      </Box>

      {loading ? (
        <LinearProgress />
      ) : (
        <TableContainer sx={{ flex: 1, overflow: "auto" }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Source</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Size</TableCell>
                <TableCell>Pulled At</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedSources.map((source) => (
                <TableRow key={source.sourceName}>
                  <TableCell>{source.sourceName}</TableCell>
                  <TableCell sx={{ color: "text.secondary" }}>
                    {SOURCE_TYPE_NAMES[source.sourceType] || source.sourceType}
                  </TableCell>
                  <TableCell>{statusChip(source, hasPull)}</TableCell>
                  <TableCell sx={{ color: "text.secondary" }}>
                    {formatBytes(source.file?.sizeBytes)}
                  </TableCell>
                  <TableCell sx={{ color: "text.secondary" }}>
                    {formatDateTime(source.file?.lastModified)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};
